import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    'Software Development',
    'Fintech Solutions',
    'Web Design',
    'UI/UX Design',
    'System Integration',
    'Cybersecurity',
  ];

  return (
    <footer data-testid="footer" className="bg-[#02050A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#FF5E1A] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">M</span>
              </div>
              <div>
                <span className="text-white font-bold text-lg">Mohtech</span>
                <span className="text-[#A1B0CC] text-sm block -mt-1">Cyber Solutions</span>
              </div>
            </Link>
            <p className="text-[#A1B0CC] text-sm leading-relaxed mb-6">
              Building smart digital solutions for modern businesses. Specializing in fintech, 
              automation, and custom software development.
            </p>
            <div className="flex gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="w-10 h-10 bg-[#0A1121] rounded-lg flex items-center justify-center text-[#A1B0CC] hover:text-[#FF5E1A] hover:border-[#FF5E1A]/40 border border-white/5 transition-all"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-twitter"
                className="w-10 h-10 bg-[#0A1121] rounded-lg flex items-center justify-center text-[#A1B0CC] hover:text-[#FF5E1A] hover:border-[#FF5E1A]/40 border border-white/5 transition-all"
              >
                <Twitter size={18} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 bg-[#0A1121] rounded-lg flex items-center justify-center text-[#A1B0CC] hover:text-[#FF5E1A] hover:border-[#FF5E1A]/40 border border-white/5 transition-all"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 bg-[#0A1121] rounded-lg flex items-center justify-center text-[#A1B0CC] hover:text-[#FF5E1A] hover:border-[#FF5E1A]/40 border border-white/5 transition-all"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-[#A1B0CC] hover:text-[#FF5E1A] text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-[#A1B0CC] hover:text-[#FF5E1A] text-sm transition-colors"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#FF5E1A] mt-1 flex-shrink-0" />
                <span className="text-[#A1B0CC] text-sm">Nairobi, Kenya</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#FF5E1A] flex-shrink-0" />
                <a
                  href="tel:+254721677360"
                  data-testid="footer-phone"
                  className="text-[#A1B0CC] hover:text-white text-sm transition-colors"
                >
                  +254 721 677360
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#FF5E1A] flex-shrink-0" />
                <a
                  href="mailto:info@mohtech-cyber.co.ke"
                  data-testid="footer-email"
                  className="text-[#A1B0CC] hover:text-white text-sm transition-colors"
                >
                  info@mohtech-cyber.co.ke
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#5B6B8A] text-sm">
            © {currentYear} Mohtech Cyber Solutions. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-[#5B6B8A] hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#5B6B8A] hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
